import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * The machine-hall hardware.
 *
 * These four are the vocabulary the CrystalDEX landing page uses to label things, ported so
 * the dashboard speaks it too. They are deliberately dumb: no data fetching, no state, no
 * layout opinions beyond their own box. What they carry is the *material* language — steel
 * plates, brass, status lamps — which is what makes a screen read as instrumentation rather
 * than as a web page.
 *
 * The division of labour across the redesign is: hardware here owns the chrome, and the
 * data zones stay flat and quiet. A riveted housing around a table would cost legibility
 * for nothing, so nothing here is meant to go behind data.
 */

// ─────────────────────────── Plate ───────────────────────────

type PlateTone = 'steel' | 'brass' | 'enamel'

const PLATE_TONE: Record<PlateTone, { bg: string; fg: string; shadow: string }> = {
  steel: { bg: 'var(--m-steel)', fg: 'var(--plate-fg)', shadow: 'var(--plate-textshadow)' },
  // Brass takes near-black text, not the brass-text token: it sits ON the material, where the
  // constraint is contrast against gold rather than against the page.
  brass: {
    bg: 'var(--m-brass)',
    fg: 'var(--plate-on-brass)',
    shadow: 'var(--plate-on-brass-textshadow)',
  },
  enamel: {
    bg: 'var(--m-enamel)',
    fg: 'var(--plate-on-enamel)',
    shadow: 'var(--plate-on-enamel-textshadow)',
  },
}

/**
 * An engraved label plate — the landing page's `ST-03 · ENGINE` chip.
 *
 * 2px radius on purpose. A plate is milled metal; rounding it further turns it into a
 * button, and it is not one.
 */
export function Plate({
  children,
  tone = 'steel',
  className,
}: {
  children: ReactNode
  tone?: PlateTone
  className?: string
}) {
  const t = PLATE_TONE[tone]

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[2px] px-2.5 py-1',
        'label whitespace-nowrap',
        className,
      )}
      style={{
        background: t.bg,
        color: t.fg,
        textShadow: t.shadow,
        boxShadow: 'var(--plate-shadow)',
      }}
    >
      {children}
    </span>
  )
}

// ─────────────────────────── Lamp ───────────────────────────

export type LampState = 'live' | 'pending' | 'fault' | 'off'

const LAMP: Record<LampState, { fill: string; glow: string; word: string }> = {
  // Only `live` glows. A glow that every state shares stops meaning "this is running" and
  // becomes decoration — which is the same rule the ambient-motion layer follows.
  live: { fill: 'var(--color-fluid)', glow: 'var(--fluid-glow)', word: 'Valve open' },
  pending: { fill: 'var(--color-brass)', glow: 'none', word: 'Standby' },
  fault: { fill: 'var(--color-fault)', glow: 'none', word: 'Fault' },
  off: { fill: 'var(--color-line-strong)', glow: 'none', word: 'Valve closed' },
}

/**
 * A status bead.
 *
 * `label` replaces the state's own word when the caller has something more specific to say
 * — a network name, for instance. Pass `label={null}` for the bead alone.
 */
export function Lamp({
  state,
  label,
  className,
}: {
  state: LampState
  label?: ReactNode
  className?: string
}) {
  const l = LAMP[state]
  const text = label === undefined ? l.word : label

  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <span
        aria-hidden
        className="size-2.5 shrink-0 rounded-full transition-[background,box-shadow] duration-200"
        style={{
          // The dome highlight makes it read as a glass bead rather than a flat dot.
          background: `var(--lamp-dome), ${l.fill}`,
          boxShadow: ['var(--lamp-seat)', l.glow].filter((s) => s !== 'none').join(', '),
        }}
      />
      {text != null && text !== '' && (
        // `role="status"` so a change of state is announced. The bead itself is aria-hidden,
        // so without this the state would be visible and inaudible.
        <span role="status" className="label">
          {text}
        </span>
      )}
    </span>
  )
}

// ─────────────────────────── Chip ───────────────────────────

type ChipTone = 'neutral' | 'enamel' | 'brass' | 'fluid' | 'fault'

const CHIP_TONE: Record<ChipTone, string> = {
  neutral: 'text-faint ring-line',
  // Each tone pairs the darkened `-text` token with the vivid `-edge` for the ring. Using
  // the material hue for the text instead is the mistake theme.css warns about — gold
  // lettering at 2.1:1.
  enamel: 'text-accent ring-accent-edge',
  brass: 'text-brass-text ring-brass-edge',
  fluid: 'text-fluid-text ring-fluid-edge',
  fault: 'text-fault-text ring-fault-edge',
}

/** A tag pill. Transparent by design — it annotates a surface rather than covering it. */
export function Chip({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode
  tone?: ChipTone
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ring-1',
        'label whitespace-nowrap',
        CHIP_TONE[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

// ─────────────────────────── Station ───────────────────────────

/**
 * A section header, as the landing page draws one: an engraved station plate, a valve lamp,
 * and whatever action belongs to the region pushed to the right.
 *
 * It replaces a conventional page title. The heading itself is rendered visually hidden
 * alongside it, because a metal plate is not a heading to a screen reader and the document
 * still needs exactly one h1 per page.
 */
export function Station({
  index,
  name,
  state = 'live',
  action,
  heading = false,
  className,
}: {
  index: number
  name: string
  state?: LampState
  action?: ReactNode
  /** Renders the visually-hidden h1. Set on the page's primary station only. */
  heading?: boolean
  className?: string
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', className)}>
      {heading && <h1 className="sr-only">{name}</h1>}
      <Plate tone="steel">
        ST-{String(index).padStart(2, '0')} · {name}
      </Plate>
      <Lamp state={state} />
      {action && <div className="ml-auto">{action}</div>}
    </div>
  )
}
